import React, { useState } from 'react'
import { Card, Form, Input, InputNumber, DatePicker, Select, Button, Space, Typography, message, Divider } from 'antd'
import { PlusOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../../../shared/api/axios-instance'

const { Title } = Typography

const MILESTONE_OPTIONS = [
    { label: 'Profile Completed', value: 'PROFILE_COMPLETED' },
    { label: 'First RFQ', value: 'FIRST_RFQ' },
    { label: 'First Bid', value: 'FIRST_BID' },
    { label: 'First Order', value: 'FIRST_ORDER' },
]

interface MilestoneForm {
    sequence: number
    milestone_identifier: string
    reward_value_rupees: number // In rupees for UX, converted to paise on submit
}

export const CampaignCreateScreen: React.FC = () => {
    const navigate = useNavigate()
    const [form] = Form.useForm()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [milestones, setMilestones] = useState<MilestoneForm[]>([
        { sequence: 1, milestone_identifier: 'PROFILE_COMPLETED', reward_value_rupees: 100 },
    ])

    const addMilestone = () => {
        setMilestones([
            ...milestones,
            {
                sequence: milestones.length + 1,
                milestone_identifier: 'FIRST_RFQ',
                reward_value_rupees: 200,
            },
        ])
    }

    const removeMilestone = (index: number) => {
        if (milestones.length <= 1) {
            message.warning('At least one milestone is required')
            return
        }
        const updated = milestones.filter((_, i) => i !== index)
        // Re-sequence
        setMilestones(updated.map((m, i) => ({ ...m, sequence: i + 1 })))
    }

    const updateMilestone = (index: number, field: keyof MilestoneForm, value: any) => {
        const updated = [...milestones]
        updated[index] = { ...updated[index], [field]: value }
        setMilestones(updated)
    }

    const handleSubmit = async (values: any) => {
        try {
            setIsSubmitting(true)

            const payload = {
                campaign_code: values.campaign_code,
                campaign_name: values.campaign_name,
                reward_type: 'WALLET',
                start_date: values.date_range[0].format('YYYY-MM-DD'),
                end_date: values.date_range[1].format('YYYY-MM-DD'),
                priority: values.priority || 0,
                milestones: milestones.map((m) => ({
                    sequence: m.sequence,
                    milestone_identifier: m.milestone_identifier,
                    reward_value: m.reward_value_rupees, // Send as decimal
                })),
            }

            await apiClient.post('/admin/campaigns', payload)
            message.success('Campaign created successfully')
            navigate('/campaigns')
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Failed to create campaign')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div style={{ width: '100%', maxWidth: 800 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/campaigns')}>
                    Back
                </Button>
                <Title level={4} style={{ margin: 0 }}>Create Reward Campaign</Title>
            </div>

            <Card>
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    initialValues={{ reward_type: 'WALLET', priority: 0 }}
                >
                    <Form.Item
                        name="campaign_code"
                        label="Campaign Code"
                        rules={[{ required: true, message: 'Campaign code is required' }]}
                        extra="Unique identifier (e.g., LAUNCH, PONGAL2027)"
                    >
                        <Input placeholder="e.g., LAUNCH" style={{ textTransform: 'uppercase' }} />
                    </Form.Item>

                    <Form.Item
                        name="campaign_name"
                        label="Campaign Name"
                        rules={[{ required: true, message: 'Campaign name is required' }]}
                    >
                        <Input placeholder="e.g., Merchant Referral Launch" />
                    </Form.Item>

                        <Form.Item 
                            name="priority" 
                            label="Priority" 
                            style={{ flex: 1 }}
                            tooltip="Higher numbers take precedence when multiple campaigns are active simultaneously."
                        >
                            <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>

                    <Form.Item
                        name="date_range"
                        label="Campaign Duration"
                        rules={[{ required: true, message: 'Date range is required' }]}
                    >
                        <DatePicker.RangePicker style={{ width: '100%' }} />
                    </Form.Item>

                    <Divider orientation="left">Milestones & Rewards</Divider>

                    {milestones.map((milestone, index) => (
                        <Card
                            key={index}
                            size="small"
                            style={{ marginBottom: 12, backgroundColor: '#fafafa' }}
                            extra={
                                <Button
                                    type="text"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeMilestone(index)}
                                />
                            }
                            title={`Milestone ${milestone.sequence}`}
                        >
                            <Space style={{ width: '100%' }} size="large">
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: 12, color: '#666' }}>Milestone</label>
                                    <Select
                                        value={milestone.milestone_identifier}
                                        onChange={(val) => updateMilestone(index, 'milestone_identifier', val)}
                                        options={MILESTONE_OPTIONS}
                                        style={{ width: '100%' }}
                                    />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ fontSize: 12, color: '#666' }}>Reward (₹)</label>
                                    <InputNumber
                                        value={milestone.reward_value_rupees}
                                        onChange={(val) => updateMilestone(index, 'reward_value_rupees', val || 0)}
                                        min={1}
                                        prefix="₹"
                                        style={{ width: '100%' }}
                                    />
                                </div>
                            </Space>
                        </Card>
                    ))}

                    <Button
                        type="dashed"
                        icon={<PlusOutlined />}
                        onClick={addMilestone}
                        block
                        style={{ marginBottom: 24 }}
                    >
                        Add Milestone
                    </Button>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" loading={isSubmitting} size="large" block>
                            Create Campaign (Draft)
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
        </div>
    )
}
